import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Tooltip, Skeleton, Empty } from "antd";
import {
  EditOutlined, DeleteOutlined, SafetyCertificateOutlined,
  SearchOutlined, ReloadOutlined, CheckOutlined, EyeOutlined, LockOutlined,
  CheckCircleFilled, CloseCircleFilled, PlusSquareOutlined, SwapOutlined, UserOutlined,
} from "@ant-design/icons";

const ROLE_COLORS = ["#5b48e8","#0284c7","#16a34a","#d97706","#dc2626","#7c3aed","#0891b2","#be185d"];
const roleColor = (name = "") => { let h = 0; for (const c of name) h = (h + c.charCodeAt(0)) % ROLE_COLORS.length; return ROLE_COLORS[h]; };
const initials = (name = "") => name.trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "R";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("cms_token")}` });
const formatDate = (d) => { if (!d) return "—"; return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); };
const isSystemRole = (role) => role.slug === "super-admin" || role.slug === "super_admin";

/* ── Matrix constants & components ───────────────────────────────────────── */
const MATRIX_PERMS = [
  { key: "can_view",      label: "View",   icon: <EyeOutlined />,         color: "#2563eb", bg: "#eff6ff", light: "#dbeafe" },
  { key: "can_create",    label: "Add",    icon: <PlusSquareOutlined />,  color: "#16a34a", bg: "#f0fdf4", light: "#bbf7d0" },
  { key: "can_edit",      label: "Edit",   icon: <EditOutlined />,        color: "#d97706", bg: "#fffbeb", light: "#fde68a" },
  { key: "can_delete",    label: "Delete", icon: <DeleteOutlined />,      color: "#dc2626", bg: "#fef2f2", light: "#fecaca" },
  { key: "import_export", label: "I/E",    icon: <SwapOutlined />,        color: "#7c3aed", bg: "#f5f3ff", light: "#ddd6fe" },
];
const MATRIX_PERM_COL_WIDTH = "56px";
const matrixGridCols = `1fr repeat(${MATRIX_PERMS.length}, ${MATRIX_PERM_COL_WIDTH})`;

const MatrixPermDot = ({ granted, perm }) => (
  <Tooltip title={`${perm.label}: ${granted ? "Granted" : "Not granted"}`}>
    <div style={{
      width: "28px", height: "28px", borderRadius: "7px",
      background: granted ? perm.bg : "#f9fafb",
      border: `1.5px solid ${granted ? perm.light : "#e5e7eb"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "12px", color: granted ? perm.color : "#d1d5db",
      transition: "all 0.15s",
    }}>
      {granted ? <CheckCircleFilled /> : <CloseCircleFilled style={{ opacity: 0.4 }} />}
    </div>
  </Tooltip>
);

const MatrixRoleCard = ({ role, selected, onClick }) => {
  const color = roleColor(role.role_name);
  return (
    <div onClick={onClick} style={{
      background: selected ? `linear-gradient(135deg, ${color}18, ${color}08)` : "white",
      border: `2px solid ${selected ? color : "#e5e7eb"}`,
      borderRadius: "12px", padding: "16px 14px", cursor: "pointer",
      transition: "all 0.2s", width: "130px", textAlign: "center",
      boxShadow: selected ? `0 4px 12px ${color}30` : "0 1px 3px rgba(0,0,0,0.05)",
      transform: selected ? "translateY(-2px)" : "none", flexShrink: 0,
    }}>
      <div style={{
        width: "40px", height: "40px", borderRadius: "10px", margin: "0 auto 8px",
        background: selected ? `linear-gradient(135deg, ${color}, ${color}99)` : "#f3f4f6",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "14px", fontWeight: 700, color: selected ? "white" : "#9ca3af",
      }}>
        {initials(role.role_name)}
      </div>
      <div style={{ fontSize: "12.5px", fontWeight: selected ? 700 : 600, color: selected ? color : "#374151", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {role.role_name}
      </div>
      {selected && (
        <div style={{ marginTop: "6px" }}>
          <span style={{ fontSize: "10px", background: color, color: "white", padding: "2px 8px", borderRadius: "10px", fontWeight: 700 }}>Active</span>
        </div>
      )}
    </div>
  );
};

const MatrixRow = ({ name, icon, perms, depth = 0, isSubmenu, canAccess = 0 }) => {
  const hasAny = canAccess === 1 || MATRIX_PERMS.some(p => perms[p.key] === 1);
  const defaultBg = hasAny ? (isSubmenu ? "rgba(91,72,232,0.02)" : "rgba(91,72,232,0.04)") : "transparent";
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: matrixGridCols, alignItems: "center", padding: `10px 16px 10px ${16 + depth * 24}px`, background: defaultBg, borderBottom: "1px solid #f3f4f6", transition: "background 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.background = "rgba(91,72,232,0.06)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = defaultBg; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {depth > 0 && <div style={{ width: "12px", height: "12px", borderLeft: "2px solid #e5e7eb", borderBottom: "2px solid #e5e7eb", borderRadius: "0 0 0 4px", marginRight: "2px", flexShrink: 0 }} />}
        {icon && <i className={icon} style={{ color: hasAny ? "#5b48e8" : "#d1d5db", fontSize: "13px", width: "16px", flexShrink: 0 }} />}
        <span style={{ fontSize: isSubmenu ? "13px" : "13.5px", fontWeight: isSubmenu ? 400 : 600, color: hasAny ? "#111827" : "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
        {hasAny && <span style={{ fontSize: "10px", background: "#f5f3ff", color: "#5b48e8", padding: "1px 6px", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}>{MATRIX_PERMS.filter(p => perms[p.key] === 1).length}/{MATRIX_PERMS.length}</span>}
      </div>
      {MATRIX_PERMS.map(p => (
        <div key={p.key} style={{ display: "flex", justifyContent: "center" }}>
          <MatrixPermDot granted={perms[p.key] === 1} perm={p} />
        </div>
      ))}
    </div>
  );
};

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [selectedRole, setSelectedRole] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  const fetchMatrix = async (roleId) => {
    setMatrixLoading(true);
    setMatrixData(null);
    try {
      const [menuRes, roleRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/cms-menus`, { headers: authH() }),
        axios.get(`${API_BASE_URL}/cms-roles/${roleId}/menus`, { headers: authH() }),
      ]);
      const menus = menuRes.data.data || [];
      const det   = roleRes.data.data;
      const emptyPerms = () => { const p = {}; MATRIX_PERMS.forEach(x => { p[x.key] = 0; }); return p; };
      const granted = {};
      (det?.menu_access || []).forEach(m => {
        const p = emptyPerms();
        MATRIX_PERMS.forEach(x => { p[x.key] = m[x.key] ?? 0; });
        granted[m.cms_menu_id] = { perms: p, subs: {}, canAccess: m.can_access ?? 0 };
        (m.sub_menus || []).forEach(sm => {
          const sp = emptyPerms();
          MATRIX_PERMS.forEach(x => { sp[x.key] = sm[x.key] ?? 0; });
          granted[m.cms_menu_id].subs[sm.cms_sub_menu_id] = sp;
        });
      });
      const rows = menus.map(menu => ({
        id: menu.id, name: menu.menu_name, icon: menu.menu_icon,
        perms: granted[menu.id]?.perms || emptyPerms(),
        canAccess: granted[menu.id]?.canAccess ?? 0,
        subMenus: (menu.sub_menus || []).map(sm => ({
          id: sm.id, name: sm.sub_menu_name, icon: sm.sub_menu_icon,
          perms: granted[menu.id]?.subs[sm.id] || emptyPerms(),
        })),
      }));
      setMatrixData({ rows, role: det?.role });
    } catch { toast.error("Failed to load permissions"); }
    finally { setMatrixLoading(false); }
  };

  const handleSelectRole = (role) => { setSelectedRole(role); fetchMatrix(role.id); };

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cms-roles`, { headers: authH() });
      const list = (res.data.data || []).slice().sort((a, b) => a.id - b.id);
      setRoles(list);
      if (list.length > 0) { setSelectedRole(list[0]); fetchMatrix(list[0].id); }
    }
    catch { toast.error("Failed to load roles"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { window.scrollTo(0, 0); fetchRoles(); }, [fetchRoles]);

  const handleDelete = async (id, name) => {
    const r = await Swal.fire({ title: `Delete "${name}"?`, text: "This action cannot be undone.", icon: "warning", showCancelButton: true, confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280", confirmButtonText: "Yes, delete" });
    if (!r.isConfirmed) return;
    try { await axios.delete(`${API_BASE_URL}/cms-roles/${id}`, { headers: authH() }); toast.success("Role deleted"); fetchRoles(); }
    catch (err) { toast.error(err?.response?.data?.message || "Failed to delete"); }
  };

  const filtered = roles.filter(r => r.role_name?.toLowerCase().includes(search.toLowerCase()));
  const activeCount = roles.filter(r => r.is_active === 1).length;

  const getParentLabel = (role) => {
    if (role.reports_to?.length > 0) return role.reports_to;
    return null;
  };

  const thStyle = { padding: "14px 16px", textAlign: "left", fontSize: "12px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap" };

  const grantedMenus = matrixData?.rows.filter(r => r.canAccess === 1).length || 0;
  const totalMenus   = matrixData?.rows.length || 0;
  const totalGranted = matrixData?.rows.reduce((acc, r) => acc + MATRIX_PERMS.filter(p => r.perms[p.key] === 1).length, 0) || 0;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Page Header ── */}
          <div style={{ background: "linear-gradient(135deg,#5b48e8 0%,#7c3aed 60%,#764ba2 100%)", borderRadius: "16px", padding: "28px 32px", marginBottom: "28px", boxShadow: "0 8px 32px rgba(91,72,232,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,.22)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "white" }}><SafetyCertificateOutlined /></div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Roles & Permissions</h4>
                <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>Define roles, assign permissions and menu access</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={fetchRoles} style={{ width: "40px", height: "40px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", color: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}><ReloadOutlined /></button>
              <Link to="/cms/manage-roles/create" style={{ display: "flex", alignItems: "center", gap: "7px", height: "40px", padding: "0 20px", borderRadius: "10px", border: "none", background: "white", color: "#5b48e8", fontWeight: 700, fontSize: "14px", textDecoration: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                <SafetyCertificateOutlined /> Add New Role
              </Link>
            </div>
          </div>

          {/* ── Stats ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "28px" }}>
            {[
              { label: "Total Roles", value: roles.length, icon: <SafetyCertificateOutlined />, color: "#5b48e8", bg: "#f5f3ff" },
              { label: "Active Roles", value: activeCount, icon: <CheckOutlined />, color: "#16a34a", bg: "#f0fdf4" },
              { label: "Inactive Roles", value: roles.length - activeCount, icon: <LockOutlined />, color: "#dc2626", bg: "#fef2f2" },
            ].map(s => (
              <div key={s.label} style={{ background: "white", borderRadius: "14px", padding: "20px 24px", border: "1px solid #f0f0f0", boxShadow: "0 2px 12px rgba(0,0,0,.05)", display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ width: "48px", height: "48px", background: s.bg, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: s.color, fontSize: "22px" }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#111827", lineHeight: 1, marginTop: "2px" }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Table Card ── */}
          <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#5b48e8,#7c3aed)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "16px" }}><SafetyCertificateOutlined /></div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "17px", color: "#1f2937" }}>All Roles</div>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>{filtered.length} role{filtered.length !== 1 ? "s" : ""}</div>
                </div>
              </div>
              <div style={{ position: "relative", width: "280px" }}>
                <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "14px" }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search roles..." style={{ width: "100%", height: "40px", paddingLeft: "36px", paddingRight: "16px", border: "1px solid #e5e7eb", borderRadius: "10px", fontSize: "14px", outline: "none", background: "white", color: "#374151", boxSizing: "border-box" }} />
              </div>
            </div>

            {loading ? (
              <div style={{ padding: "24px" }}>{[1,2,3,4].map(i => <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: "16px" }} />)}</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>{search ? "No roles match your search" : "No roles found."}</span>} />
                {!search && <Link to="/cms/manage-roles/create" style={{ display: "inline-block", marginTop: "16px", padding: "10px 24px", borderRadius: "10px", background: "linear-gradient(135deg,#5b48e8,#7c3aed)", color: "white", fontWeight: 600, fontSize: "14px", textDecoration: "none" }}>Create First Role</Link>}
              </div>
            ) : (
              <div className="table-responsive">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "linear-gradient(135deg,#f8f9ff 0%,#f1f5f9 100%)", borderBottom: "2px solid #e2e8f0" }}>
                      <th style={{ ...thStyle, width: "40px", textAlign: "center" }}>#</th>
                      <th style={thStyle}>Role Name</th>
                      <th style={thStyle}>Role Key</th>
                      <th style={thStyle}>Description</th>
                      <th style={thStyle}>Reports To</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                      <th style={thStyle}>Created</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((role, idx) => {
                      const color = roleColor(role.role_name);
                      const isSys = isSystemRole(role);
                      const parentLabel = getParentLabel(role);
                      const rowBg = idx % 2 === 0 ? "white" : "#fafbff";
                      return (
                        <tr key={role.id} style={{ borderBottom: "1px solid #f5f5f5", background: rowBg, transition: "background 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "#f1f5f9"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                          <td style={{ padding: "14px 16px", textAlign: "center", fontSize: "13px", color: "#9ca3af", fontWeight: 600 }}>{idx + 1}</td>
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div style={{ width: "36px", height: "36px", borderRadius: "9px", flexShrink: 0, background: `linear-gradient(135deg, ${color}dd, ${color}88)`, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "12px", fontWeight: 700 }}>{initials(role.role_name)}</div>
                              <div style={{ fontSize: "14px", fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: "6px" }}>
                                {role.role_name}
                                {isSys && <span style={{ fontSize: "10px", background: "#dbeafe", color: "#2563eb", padding: "1px 7px", borderRadius: "10px", fontWeight: 600 }}>System</span>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}><code style={{ fontSize: "12px", background: "#f3f4f6", color: "#d97706", padding: "2px 8px", borderRadius: "4px", fontWeight: 600, whiteSpace: "nowrap" }}>{role.slug || "—"}</code></td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#6b7280", maxWidth: "200px" }}><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{role.description || "—"}</span></td>
                          <td style={{ padding: "14px 16px" }}>
                            {parentLabel ? (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                                {parentLabel.map(r => (
                                  <span key={r.id} style={{ background: "#f0f9ff", color: "#0284c7", border: "1px solid #bae6fd", borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{r.role_name}</span>
                                ))}
                              </div>
                            ) : <span style={{ fontSize: "12px", color: "#9ca3af" }}>Top Level</span>}
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <span style={{ background: role.is_active === 1 ? "#f0fdf4" : "#fef2f2", color: role.is_active === 1 ? "#16a34a" : "#dc2626", border: `1px solid ${role.is_active === 1 ? "#bbf7d0" : "#fecaca"}`, borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600 }}>{role.is_active === 1 ? "Active" : "Inactive"}</span>
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13px", color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(role.created_at)}</td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            {/* SuperAdmin Actions Lock logic */}
                            {/* {isSys ? (
                              <span style={{ fontSize: "12px", color: "#9ca3af", fontWeight: 600, background: "#f3f4f6", padding: "4px 14px", borderRadius: "8px" }}>Locked</span>
                            ) : (
                              <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                <Tooltip title="Edit Role">
                                  <Link to={`/cms/manage-roles/edit/${role.id}`} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><EditOutlined /></Link>
                                </Tooltip>
                                <Tooltip title="View">
                                  <Link to={`/cms/manage-roles/edit/${role.id}?view=1`} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #fde68a", background: "#fffbeb", color: "#d97706", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><EyeOutlined /></Link>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <button onClick={() => handleDelete(role.id, role.role_name)} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><DeleteOutlined /></button>
                                </Tooltip>
                              </div>
                            )} */}

                            <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                                <Tooltip title="Edit Role">
                                  <Link to={`/cms/manage-roles/edit/${role.id}`} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #e0eaff", background: "#f0f5ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><EditOutlined /></Link>
                                </Tooltip>
                                <Tooltip title="View">
                                  <Link to={`/cms/manage-roles/edit/${role.id}?view=1`} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #fde68a", background: "#fffbeb", color: "#d97706", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><EyeOutlined /></Link>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <button onClick={() => handleDelete(role.id, role.role_name)} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px" }}><DeleteOutlined /></button>
                                </Tooltip>
                              </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Role Selector ── */}
          <div style={{ background: "white", borderRadius: "16px", padding: "20px 24px", marginTop: "28px", marginBottom: "24px", border: "1px solid #f0f0f0", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "14px" }}>
              <UserOutlined style={{ marginRight: "6px" }} /> Select Role to View Permissions
            </div>
            {loading ? (
              <div style={{ display: "flex", gap: "12px" }}>
                {[1,2,3,4].map(i => <Skeleton.Button key={i} active style={{ width: "130px", height: "100px", borderRadius: "12px" }} />)}
              </div>
            ) : roles.length === 0 ? (
              <Empty description="No roles found" />
            ) : (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {roles.map(role => (
                  <MatrixRoleCard key={role.id} role={role} selected={selectedRole?.id === role.id} onClick={() => handleSelectRole(role)} />
                ))}
              </div>
            )}
          </div>

          {/* ── Stats row ── */}
          {selectedRole && matrixData && !matrixLoading && (
            <div style={{ display: "flex", gap: "14px", marginBottom: "24px", flexWrap: "wrap" }}>
              {[
                { label: "Menus with Access", value: `${grantedMenus}/${totalMenus}`, color: "#5b48e8", bg: "#f5f3ff", icon: <SafetyCertificateOutlined /> },
                { label: "Permissions Granted", value: totalGranted, color: "#16a34a", bg: "#f0fdf4", icon: <CheckCircleFilled /> },
                { label: "Permissions Restricted", value: (totalMenus * MATRIX_PERMS.length) - totalGranted, color: "#dc2626", bg: "#fef2f2", icon: <CloseCircleFilled /> },
              ].map(s => (
                <div key={s.label} style={{ background: "white", borderRadius: "12px", padding: "14px 20px", border: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: "12px", minWidth: "180px", boxShadow: "0 2px 8px rgba(0,0,0,.04)", flex: 1 }}>
                  <div style={{ width: "36px", height: "36px", background: s.bg, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: s.color, fontSize: "16px" }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: 800, color: "#111827", lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, marginTop: "2px" }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Permission Matrix ── */}
          {selectedRole ? (
            <div style={{ background: "white", borderRadius: "16px", border: "1px solid #f0f0f0", boxShadow: "0 2px 16px rgba(0,0,0,.07)", overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: matrixGridCols, padding: "14px 16px", background: "linear-gradient(135deg,#f8f9ff 0%,#f0f4ff 100%)", borderBottom: "2px solid #e8ecff", alignItems: "center" }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>Menu / Feature</div>
                {MATRIX_PERMS.map(p => (
                  <div key={p.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
                    <div style={{ width: "28px", height: "28px", borderRadius: "7px", background: p.bg, display: "flex", alignItems: "center", justifyContent: "center", color: p.color, fontSize: "13px" }}>{p.icon}</div>
                    <span style={{ fontSize: "9px", fontWeight: 700, color: p.color, textTransform: "uppercase", letterSpacing: "0.3px" }}>{p.label}</span>
                  </div>
                ))}
              </div>
              {matrixLoading ? (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <div className="spinner-border" style={{ color: "#5b48e8", width: "32px", height: "32px" }} role="status" />
                  <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "12px" }}>Loading permissions for <strong>{selectedRole.role_name}</strong>...</div>
                </div>
              ) : matrixData?.rows.length === 0 ? (
                <div style={{ padding: "60px 24px", textAlign: "center" }}>
                  <Empty description={<span style={{ color: "#9ca3af" }}>No menus configured in the system</span>} />
                </div>
              ) : (
                <div>
                  {matrixData?.rows.map(row => (
                    <div key={row.id}>
                      <MatrixRow name={row.name} icon={row.icon} perms={row.perms} depth={0} canAccess={row.canAccess} />
                      {row.subMenus.map(sub => (
                        <MatrixRow key={sub.id} name={sub.name} icon={sub.icon} perms={sub.perms} depth={1} isSubmenu />
                      ))}
                    </div>
                  ))}
                  <div style={{ padding: "12px 16px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 600 }}>Legend:</span>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>
                      <CheckCircleFilled style={{ fontSize: "13px" }} /> Granted
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#d1d5db", fontWeight: 600 }}>
                      <CloseCircleFilled style={{ fontSize: "13px" }} /> Not granted
                    </div>
                    <span style={{ marginLeft: "auto", fontSize: "11px", color: "#9ca3af" }}>
                      Viewing: <strong style={{ color: "#374151" }}>{selectedRole.role_name}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: "white", borderRadius: "16px", padding: "60px 24px", border: "1px solid #f0f0f0", textAlign: "center", boxShadow: "0 2px 16px rgba(0,0,0,.07)" }}>
              <LockOutlined style={{ fontSize: "40px", color: "#e5e7eb", marginBottom: "12px" }} />
              <div style={{ fontSize: "15px", fontWeight: 600, color: "#9ca3af" }}>Select a role above to view its permissions</div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ManageRoles;